import type { Effect, Event, Store, Tuple } from 'effector';
import { clearNode, combine } from 'effector';
import { useUnit } from 'effector-vue/composition';
import type { Ref } from 'vue';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { uiService } from '@ui';
import { userService } from '@user';
import { useCssVar, useDark } from '@vueuse/core';

import { PAGES } from './constants';
import type { Module } from './model';

/**
 * Create combine store with destroy on unmount
 */
export const useEffCombine = <T extends Tuple>(stores: T) => {
	const combined = combine<T>(stores);
	onUnmounted(() => {
		clearNode(combined, { deep: true });
	});
	return combined;
};

/**
 * Subscribe to multiple stores
 */
type UseEffStores<T> = { [K in keyof T]: T[K] extends Store<infer U> ? Ref<U> : Ref<T[K]> };
export const useEffStores = <T extends Tuple>(stores: T) => {
	return stores.map((store) => useUnit(store as Store<any>)) as UseEffStores<T>;
};

/**
 * Subscribe / unsubscribe to effector unit watcher
 */
export const useEffUnitWatch = <T>(
	unit: Effect<T, any, any> | Event<T> | Store<T>,
	fn: (data: T, prev: T | null) => void,
) => {
	const prev: Ref<T | null> = ref(null);
	const unwatch = unit.watch((value) => {
		fn(value, prev.value);
		prev.value = value;
	});
	onUnmounted(() => {
		unwatch();
	});
};

interface ModuleInitArgs {
	module: Module;
}

// Initialise data on page init
export const usePageInit = () => {
	const isDark = useDark();
	const [user, busy] = useEffStores([userService.$userInfo, userService.getUserInfoFx.pending]);
	const backgroundColor = useCssVar('--app-background-color');

	const color = computed(() => (isDark.value ? '#000000' : '#ffffff'));

	useEffUnitWatch(userService.$userInfo, ({ isLoggedIn }) => {
		backgroundColor.value = isLoggedIn ? color.value : 'transparent';
	});

	uiService.init();

	return { busy, user };
};

// Initialise data on module init
export const useModuleInit = ({ module }: ModuleInitArgs) => {
	const { user, busy } = usePageInit();
	const router = useRouter();

	const stores = useEffCombine([userService.$userInfo, userService.$userPermissions]);

	useEffUnitWatch(stores, ([info, permissions]) => {
		if (info.isLoggedIn) {
			const enabled = module.enabled(permissions);
			if (!enabled) void router.replace({ name: PAGES.MODULES });
		}
	});

	return { busy, user };
};

// react on key down event
export const useKeyDown = (cb: (e: KeyboardEvent) => void) => {
	const listener = (event: KeyboardEvent) => {
		cb?.(event);
	};

	onMounted(() => {
		document.addEventListener('keydown', listener);
	});
	onUnmounted(() => {
		document.removeEventListener('keydown', listener);
	});
};

/**
 * Click-to-edit field: a read-only display that turns into an input.
 *
 * Use it with a `ref="displayRef"` display element and a `ref="inputRef"` input:
 * - `v-bind="displayAttrs"` on the display makes it a keyboard-operable button (role, tabindex, click, and
 *   Enter or Space start editing).
 * - `v-bind="inputAttrs"` on the input takes it out of the tab order while it is not being edited, ends the
 *   edit on blur, and ends it on Enter.
 *
 * Editing starts on keyup, like a native button does for Space, because starting to edit moves focus to the
 * input, which would otherwise receive the keyup of the same key press and, with its own Enter handler, close
 * the field again at once.
 */
export const useClickToEdit = () => {
	const inputRef = ref<HTMLInputElement>();
	const displayRef = ref<HTMLElement>();
	const editing = ref(false);

	const onEdit = () => {
		editing.value = true;
		// still works while the input has tabindex -1, which it has until the next render
		inputRef.value?.focus();
		inputRef.value?.select();
	};

	const onBlur = () => (editing.value = false);

	// Enter ends the edit. Focus goes back to the display so that the keyboard user keeps their place and the
	// (now invisible) input cannot take further keystrokes.
	const commit = async () => {
		editing.value = false;
		await nextTick();
		displayRef.value?.focus();
	};

	const isActivationKey = (event: KeyboardEvent) => event.key === 'Enter' || event.key === ' ';

	const onKeydown = (event: KeyboardEvent) => {
		// stop Space from scrolling the page
		isActivationKey(event) && event.preventDefault();
	};

	const onKeyup = (event: KeyboardEvent) => {
		isActivationKey(event) && onEdit();
	};

	const displayAttrs = { role: 'button', tabindex: 0, onClick: onEdit, onKeydown, onKeyup };

	const inputAttrs = computed(() => ({
		tabindex: editing.value ? 0 : -1,
		onBlur,
		onKeyup: (event: KeyboardEvent) => {
			if (event.key === 'Enter') void commit();
		},
	}));

	return { inputRef, displayRef, editing, displayAttrs, inputAttrs };
};
